import React, { useState } from "react";

import WarningImage from "@common/assets/images/warning.png";
import Button from "@common/components/Button/Button";
import Input from "@common/components/Input/Input";
import { ModalProps } from "@common/components/Modal/Modal";
import { ModalHeaderProps } from "@common/components/Modal/ModalHeader/ModalHeader";
import { Flex } from "@common/styles/reusable/Flex/Flex.styles";
import identifiers from "@common/util/identifiers.json";

import * as Styled from "./ModalBody.styles";

interface Props {
  selectedWorkspaceName?: string;
  deleteWorkspace: () => void;
  loading: boolean;
  Modal: React.ForwardRefExoticComponent<ModalProps & ModalHeaderProps & React.RefAttributes<unknown>>;
  internalProps?: ModalProps;
}

export default function DeleteWorkspaceModalBody({
  selectedWorkspaceName,
  deleteWorkspace,
  loading,
  Modal,
  internalProps,
}: Props) {
  const [workspaceName, setWorkspaceName] = useState<string>("");

  const isButtonDisabled = workspaceName !== selectedWorkspaceName;

  const handleDeleteWorkspace = (e?: React.MouseEvent<Element, MouseEvent>) => {
    e?.preventDefault();
    deleteWorkspace();
  };

  return (
    <Modal
      {...internalProps}
      testId={identifiers["general.modal.header.deleting"]}
      heading={`Delete this workspace?`}
      buttonElement={
        <Button
          type="button"
          size="small"
          color="red"
          disabled={isButtonDisabled || loading}
          testId={identifiers["general.modal.button.deleteWorkspace"]}
          tooltip={{
            position: "top",
            text: "Please enter correct workspace name to enable deleting",
            disabled: !isButtonDisabled || loading,
          }}
          loadingText={loading && "Deleting..."}
          onClick={(e) => handleDeleteWorkspace(e)}
        >
          Delete this workspace
        </Button>
      }
    >
      <Flex $column>
        <img
          style={{ alignSelf: "center", marginBottom: "2.4rem" }}
          src={WarningImage}
          alt="Warning"
        />
        <Styled.Subtitle>
          {`This action is permanent and cannot be undone.\nDeleting ${selectedWorkspaceName} will permanently remove all associated apps, tickets, and data. This will also revoke access for all team members.\nTo confirm, please type the workspace name below:`}
        </Styled.Subtitle>

        <Styled.Form onSubmit={(event) => event.preventDefault()}>
          <Input
            type="text"
            label="Workspace name"
            value={workspaceName}
            onChange={(e: React.FormEvent<HTMLInputElement>) => setWorkspaceName(e.currentTarget.value)}
            testId={identifiers["general.modal.input.workspaceName"]}
            autoFocus
          />
        </Styled.Form>
      </Flex>
    </Modal>
  );
}
